import { ordinarySearchClick } from '../../features/search/searchSession';
import { useSearchSession } from '../../features/search/SearchSessionContext';
import { useLocation } from 'react-router-dom';
import { useUserStore } from '../../stores/userStore';
import { rememberSearchSelection } from '../../features/search/continuationArtist';
import React from 'react';
import RecoverableImage from '../../features/images/RecoverableImage';
import { Link } from 'react-router-dom';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { useNavigateWithScrollMemory } from '../../hooks/useNavigateWithScrollMemory';
import { REFERRER_TYPE } from '../../constants/gaEnums';
import HydratedPlaylistArtwork from './HydratedPlaylistArtwork';
import baseline, { appTextStyle } from '../../app-ui/baseline';
import explicitIcon from '../../assets/explicit_icon.svg';
import './SearchResultRow.css';

// These rows are search-only. Existing artist and playlist cards retain their own actions.
export default function SearchResultRow({
  item,
  kind,
  summary = false,
  resultPosition = 1,
  resultCategory,
  assistMeta,
  onOpen,
  toOverride,
  reviewTone = false,
}) {
  const session = useSearchSession();
  const location = useLocation();
  const userId = useUserStore((s) => s.user?.id);
  const assist = assistMeta !== undefined;
  const { locale } = useWebLocale();
  const navigate = useNavigateWithScrollMemory();
  const artist = kind === 'artist';
  const album = kind === 'album';
  const image = (album ? item.albumImageUrl : item.artistImageUrl) || item.imageUrl;
  const id = item.id ?? item.artistId;
  const artistPath = item.slug
    ? `/artists/${encodeURIComponent(item.slug)}${item.artistId != null ? '' : `?source_id=${encodeURIComponent(id)}`}`
    : `/artists/${item.artistSource?.toLowerCase()}/${encodeURIComponent(id)}`;
  const albumPath = item.slug
    ? `/reviews/${encodeURIComponent(item.slug)}${item.albumId ? '' : `?source_id=${encodeURIComponent(item.sourceId)}`}`
    : `/reviews/${item.albumSource?.toLowerCase()}/${encodeURIComponent(item.sourceId)}`;
  const to =
    toOverride ||
    (album
      ? albumPath
      : artist
        ? artistPath
        : `/lists/${encodeURIComponent(item.slug || item.id)}`);
  const state = album
    ? { sourceId: item.sourceId, sourceType: item.albumSource, referrerType: REFERRER_TYPE.SEARCH }
    : artist
      ? { sourceId: id, sourceType: item.artistSource, referrerType: REFERRER_TYPE.SEARCH }
      : { referrerType: REFERRER_TYPE.SEARCH };
  const title = artist ? item.name : item.title;
  const type =
    locale === 'en'
      ? item.type === 'ALBUM'
        ? 'Album playlist'
        : 'Track playlist'
      : item.type === 'ALBUM'
        ? '앨범 플리'
        : '곡 플리';
  const metadata = artist
    ? locale === 'en'
      ? 'Artist'
      : '아티스트'
    : `${type} · ${item.username || item.userNickname || 'postpop'}${summary ? ` · ${locale === 'en' ? `${item.commentCount ?? 0} comments` : `댓글 ${item.commentCount ?? 0}`}` : ''}`;
  const enabled = !artist || (id != null && id !== 'unknown');
  const native = baseline.search[summary ? 'summary' : 'detail'];
  const row = summary ? native.previewRow : native.row;
  const cover = summary ? native.previewImage : album ? native.albumImage : native.image;
  const style = {
    '--search-cover': `${kind === 'playlist' ? baseline.search.summary.previewImage.width : cover.width}px`,
    '--search-row-height': `${row.minHeight}px`,
    '--search-row-gap': `${row.gap}px`,
    '--search-row-padding': `${row.paddingVertical}px`,
    '--search-copy-gap': `${(summary ? native.previewCopy : native.text).gap}px`,
    '--search-cover-radius': `${baseline.tokens.radius.cover}px`,
  };
  const content = (
    <>
      {artist || album ? (
        <span className="pp-search-row-artwork" aria-hidden="true">
          {image ? <RecoverableImage src={image} alt="" loading="lazy" fallback={<>P</>} /> : 'P'}
        </span>
      ) : (
        <HydratedPlaylistArtwork playlist={item} />
      )}
      <span className="pp-search-row-copy">
        <span
          className="pp-search-row-title"
          style={appTextStyle(artist ? baseline.contentText.artist : baseline.contentText.title)}
        >
          {title}
          {album && item.contentRating === 'EXPLICIT' && (
            <img className="pp-search-row-explicit" src={explicitIcon} alt="Explicit" />
          )}
        </span>
        <span className="pp-search-row-meta" style={appTextStyle(baseline.contentText.metadata)}>
          {assist ? (
            assistMeta
          ) : album ? (
            <>
              <span
                className="pp-search-row-artist"
                style={appTextStyle(baseline.contentText.artist)}
              >
                {item.artist?.name ?? (locale === 'en' ? 'Artist' : '아티스트')}
                {item.releaseDate ? ' · ' : ''}
              </span>
              {item.releaseDate ?? ''}
            </>
          ) : (
            metadata
          )}
        </span>
      </span>
      {assist && (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
      )}
    </>
  );
  const className = `pp-search-result-row ${assist ? 'pp-search-assist-row' : ''} ${reviewTone ? 'pp-search-review-suggestion' : ''} ${album ? 'pp-search-album-row' : artist ? 'pp-search-artist-row' : 'pp-search-playlist-row'} ${summary ? 'pp-search-summary-row' : 'pp-search-detail-row'}`;
  return enabled ? (
    <Link
      to={to}
      state={state}
      className={className}
      style={style}
      onClick={(event) => {
        if (!ordinarySearchClick(event)) return;
        if (onOpen) onOpen();
        else if (summary && location.pathname === '/search/summary') {
          rememberSearchSelection(
            kind,
            item,
            new URLSearchParams(location.search).get('q'),
            userId,
          );
        }
        if (!onOpen)
          session?.open({
            query: new URLSearchParams(location.search).get('q'),
            kind,
            item,
            position: resultPosition,
            category: resultCategory,
          });
        event.preventDefault();
        navigate(to, { state });
      }}
    >
      {content}
    </Link>
  ) : (
    <div className={className} style={style}>
      {content}
    </div>
  );
}
